import {
	UseFetchHookOutput,
	UseFetchInput,
	useFetch as useFetchCore,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { AppHelper, LoggerHelper } from '@helpers';
import { useBlockStepManager } from '@modules';
import { LoginBlock, useLoginStore } from '@store';
import { BaseServiceOutput } from '@types';

import { useLoader } from '../useLoader';
import { useModal } from '../useModal';

interface CustomUseFetchInput<TInput, TOutput> extends Omit<UseFetchInput<TInput, TOutput>, 'onError'> {
	onError?: (err: BaseServiceOutput<TInput, TOutput>) => void;
}

export interface UseFetchInputProps<TInput, TOutput> {
	input: CustomUseFetchInput<TInput, TOutput>;
	showGlobalLoader?: boolean;
	showGenericErrorModal?: boolean;
	showLoggerOverlay?: boolean;
	requestName?: string;
}

export const useFetch = <TInput, TOutput>(
	props: UseFetchInputProps<TInput, TOutput>
): UseFetchHookOutput<TInput, TOutput> => {
	const {
		input,
		showGlobalLoader = true,
		showGenericErrorModal = true,
		showLoggerOverlay = true,
		requestName,
	} = props;
	const { fetchFn, onSuccess, onError } = input;

	const globalLoader = useLoader('global');
	const genericErrorModal = useModal('genericError');
	const sessionExpiredModal = useModal('sessionExpired');

	const alias = useLoginStore((state) => state.form?.alias);
	const resetLoginFormState = useLoginStore((state) => state.resetFormState);
	const { goto } = useBlockStepManager();

	return useFetchCore<TInput, TOutput>({
		fetchFn,
		onError: (err) => {
			const errorOutput = err as BaseServiceOutput<TInput, TOutput>;

			showLoggerOverlay &&
				LoggerHelper.error('Api', 'useFetch > Error fetching: ' + requestName, errorOutput, {
					overlay: { show: true },
				});

			onError?.(errorOutput);

			if (errorOutput.metadata?.errors && AppHelper.isSessionExpired(errorOutput.metadata?.errors[0].code)) {
				/* if session is expired,
				- show session expired modal
				- reset form */
				sessionExpiredModal.show();
				resetLoginFormState({ alias });
				goto(LoginBlock.Alias);
			} else if (showGenericErrorModal) {
				/* in any other case, show generic error modal */
				genericErrorModal.show();
			}
		},
		onSuccess,
		options: {
			loadingFunctions: showGlobalLoader
				? {
						hide: globalLoader.hide,
						show: globalLoader.show,
				  }
				: undefined,
		},
	});
};
